import { useState, useEffect, useRef } from 'react';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import FadeImage from '../../shared/components/FadeImage';
import PartnerLogo from '../../shared/components/PartnerLogo';
import { C, t } from '../../shared/constants/dashboardConstants';
import {
  getPartnerDetail, isPartnerApiUnavailable, PARTNER_TYPE_LABEL, partnerTypes,
} from '../../api/partnerApi';
import { getTalentDetail } from '../../api/talentApi';

/** 연결된 배우 사진을 한 번에 몇 개씩 받아올지 */
const PHOTO_FETCH_BATCH = 6;

/* ── 공통 조각 ── */

function Section({ title, count, children }) {
  return (
    <section style={{ marginTop: '48px' }}>
      <h2 style={{ ...t('22px', 700, C.bk), margin: '0 0 20px' }}>
        {title}
        {count != null && <span style={{ ...t('15px', 400, C.sub), marginLeft: '8px' }}>총 {count}명</span>}
      </h2>
      {children}
    </section>
  );
}

function CardGrid({ children, min = '200px' }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}, 1fr))`, gap: '16px' }}>
      {children}
    </div>
  );
}

function ThumbCard({ imageUrl, title, subtitle, onClick, ratio = '3 / 4', placeholder = 'image' }) {
  return (
    <div
      onClick={onClick}
      style={{
        border: `1px solid ${C.g200}`, borderRadius: '12px', overflow: 'hidden',
        background: C.white, cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{
        width: '100%', aspectRatio: ratio, background: C.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {/* 제목은 바로 아래 캡션에 있으므로, 이미지가 없으면 기본 아이콘만 둔다 */}
        <FadeImage src={imageUrl} alt={title ?? ''} size="medium" placeholder={placeholder} background={C.bg} />
      </div>
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span style={{ ...t('15px', 600, C.bk), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        {subtitle && (
          <span style={{ ...t('13px', 400, C.sub), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

const ICONS = {
  email: 'M4 4h16v16H4zM4 4l8 8 8-8',
  homepage: 'M10 14L21 3M21 3v7M21 3h-7M21 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h5',
  instagram: 'M4 4h16v16H4zM16 11.4A4 4 0 1112.6 8 4 4 0 0116 11.4z',
  youtube: 'M22 12s0-4-.5-5.5C21 5 20 4.5 19 4.4 16.5 4 12 4 12 4s-4.5 0-7 .4C4 4.5 3 5 2.5 6.5 2 8 2 12 2 12s0 4 .5 5.5C3 19 4 19.5 5 19.6 7.5 20 12 20 12 20s4.5 0 7-.4c1-.1 2-.6 2.5-2.1.5-1.5.5-5.5.5-5.5zM10 15V9l5 3z',
};

/** 핸들(@castar)로 넣어도, 주소를 통째로 넣어도 링크가 되도록 */
const socialHref = (value, base) => {
  const v = String(value).trim();
  if (/^https?:\/\//i.test(v)) return v;
  return `${base}${v.replace(/^@/, '')}`;
};

function ContactRow({ type, label, value, href }) {
  const body = (
    <>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <path d={ICONS[type]} stroke={C.sub} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span style={{ ...t('14px', 400, C.sub), width: '72px', flexShrink: 0 }}>{label}</span>
      <span style={{ ...t('14px', 400, C.bk), wordBreak: 'break-all' }}>{value}</span>
    </>
  );
  const style = { display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', textDecoration: 'none' };
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" style={style}>{body}</a>
    : <div style={style}>{body}</div>;
}

/**
 * 소속 배우 그리드 (제작사·소속사·교육기관이 공유)
 *
 * 연결 대상은 파트너 하위 목록의 행 id가 아니라 배우 프로필 id다.
 * 어드민에서 talentProfileId를 넣어준 배우만 상세로 넘어간다.
 */
function TalentGrid({ talents, navigate }) {
  return (
    <CardGrid>
      {talents.map((a, i) => (
        <ThumbCard
          key={a.id ?? i}
          imageUrl={a.profileImageUrl}
          placeholder="talent"
          title={a.name}
          subtitle={a.roleSummary}
          onClick={a.talentProfileId ? () => navigate(`/talents/${a.talentProfileId}`) : undefined}
        />
      ))}
    </CardGrid>
  );
}

/* ── 종류별 하단 구성 ── */

/**
 * 종류마다 어떤 섹션을 그릴지.
 * 한 업체가 여러 종류를 가질 수 있으므로 합집합으로 모아 각 섹션을 한 번씩만 그린다.
 * (제작사 + 소속사면 소속 배우가 두 번 나오면 안 된다)
 */
const SECTION_KINDS_BY_TYPE = {
  PRODUCTION: ['filmography', 'talents'],
  AGENCY: ['talents'],
  ACADEMY: ['departments', 'talents', 'works'],
  ETC: ['benefits', 'gallery'],
};

/** 그리는 순서 — 종류를 어떻게 조합해도 이 차례를 따른다 */
const SECTION_ORDER = ['filmography', 'talents', 'departments', 'works', 'benefits', 'gallery'];

function PartnerSections({ partner, navigate }) {
  const types = partnerTypes(partner);
  const kinds = new Set(types.flatMap(type => SECTION_KINDS_BY_TYPE[type] ?? []));

  const filmography = partner.filmography ?? [];
  const talents = partner.talents ?? [];
  const departments = partner.departments ?? [];
  const works = partner.works ?? [];
  const benefits = partner.benefits ?? [];
  const gallery = partner.gallery ?? [];

  // 교육기관 한 종류일 때만 '재학·졸업'이라 부른다
  const talentsTitle = types.length === 1 && types[0] === 'ACADEMY' ? '재학·졸업 배우' : '소속 배우';

  const section = {
    filmography: filmography.length > 0 && (
      <Section key="filmography" title="필모그래피" count={filmography.length}>
        <CardGrid>
          {filmography.map((f, i) => (
            <ThumbCard
              key={f.id ?? i}
              imageUrl={f.imageUrl}
              title={f.title}
              subtitle={f.year ? `${f.year}` : f.category}
              ratio="16 / 9"
            />
          ))}
        </CardGrid>
      </Section>
    ),

    talents: talents.length > 0 && (
      <Section key="talents" title={talentsTitle} count={talents.length}>
        <TalentGrid talents={talents} navigate={navigate} />
      </Section>
    ),

    departments: departments.length > 0 && (
      <Section key="departments" title="학과 · 학회">
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {departments.map((d, i) => (
            <span
              key={d.id ?? i}
              style={{
                padding: '10px 16px', borderRadius: '10px',
                border: `1px solid ${C.g200}`, background: C.white, ...t('15px', 500, C.bk),
              }}
            >
              {d.name}
              {d.kind && <span style={{ ...t('13px', 400, C.sub), marginLeft: '6px' }}>{d.kind}</span>}
            </span>
          ))}
        </div>
      </Section>
    ),

    works: works.length > 0 && (
      <Section key="works" title="작품">
        <CardGrid>
          {works.map((w, i) => (
            <ThumbCard key={w.id ?? i} imageUrl={w.imageUrl} title={w.title} subtitle={w.year} ratio="16 / 9" />
          ))}
        </CardGrid>
      </Section>
    ),

    benefits: benefits.length > 0 && (
      <Section key="benefits" title="할인 혜택">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {benefits.map((b, i) => (
            <div
              key={b.id ?? i}
              style={{
                border: `1px solid ${C.g200}`, borderRadius: '12px', padding: '18px 20px',
                display: 'flex', flexDirection: 'column', gap: '6px', background: C.white,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={t('16px', 700, C.bk)}>{b.title}</span>
                {b.discount && (
                  <span style={{
                    padding: '3px 10px', borderRadius: '100px',
                    background: C.point, ...t('13px', 700, C.bk),
                  }}>
                    {b.discount}
                  </span>
                )}
              </div>
              {b.description && (
                <span style={{ ...t('14px', 400, C.sub), lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {b.description}
                </span>
              )}
              {b.condition && <span style={t('13px', 400, C.g400)}>조건 · {b.condition}</span>}
            </div>
          ))}
        </div>
      </Section>
    ),

    gallery: gallery.length > 0 && (
      <Section key="gallery" title="갤러리">
        <CardGrid min="220px">
          {gallery.map((g, i) => (
            <div key={g.id ?? i} style={{
              width: '100%', aspectRatio: '4 / 3', borderRadius: '12px', overflow: 'hidden',
              border: `1px solid ${C.g200}`, background: C.bg,
            }}>
              {g.url && <FadeImage src={g.url} alt={g.caption ?? ''} size="medium" background={C.bg} />}
            </div>
          ))}
        </CardGrid>
      </Section>
    ),
  };

  return <>{SECTION_ORDER.filter(k => kinds.has(k)).map(k => section[k])}</>;
}
/* ── 페이지 ── */

export default function PartnerDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 사진을 이미 찾아본 배우 — 사진이 없는 배우를 계속 다시 부르지 않도록
  const photoTriedRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPartnerDetail(slug)
      .then(d => { if (!cancelled) { setPartner(d); setError(null); } })
      .catch(err => {
        if (cancelled) return;
        // 상세에서의 404는 '엔드포인트 없음'이 아니라 '그런 파트너가 없음'이다.
        const code = err.response?.status;
        setError(code === 404
          ? '파트너를 찾을 수 없습니다.'
          : isPartnerApiUnavailable(err)
            ? '파트너 정보를 준비 중입니다.'
            : `정보를 불러오지 못했습니다. (HTTP ${code ?? '네트워크'})`);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => { photoTriedRef.current = new Set(); }, [slug]);

  /**
   * 소속 배우 사진 보충.
   *
   * 어드민이 사진을 따로 올리지 않으면 profileImageUrl이 비어 실루엣만 나온다.
   * 캐스타 계정에 연결된 배우는 본인 프로필 사진이 있으므로 그것을 가져다 쓴다.
   * (서버가 연결된 배우 사진을 채워주면 이 보충은 저절로 동작하지 않는다)
   */
  useEffect(() => {
    const missing = (partner?.talents ?? []).filter(t => (
      t.talentProfileId && !t.profileImageUrl && !photoTriedRef.current.has(t.talentProfileId)
    ));
    if (missing.length === 0) return undefined;
    missing.forEach(t => photoTriedRef.current.add(t.talentProfileId));

    let cancelled = false;
    (async () => {
      const found = new Map();
      for (let i = 0; i < missing.length; i += PHOTO_FETCH_BATCH) {
        const batch = missing.slice(i, i + PHOTO_FETCH_BATCH);
        const pairs = await Promise.all(batch.map(t => (
          // 동기 예외까지 삼키도록 감싼다 — 사진 보충 실패는 화면을 막지 않는다
          Promise.resolve()
            .then(() => getTalentDetail(t.talentProfileId))
            .then(d => [t.talentProfileId, d?.profileImageUrl ?? null])
            .catch(() => [t.talentProfileId, null])
        )));
        if (cancelled) return;
        pairs.forEach(([id, url]) => { if (url) found.set(id, url); });
      }
      if (cancelled || found.size === 0) return;
      setPartner(prev => (prev ? {
        ...prev,
        talents: (prev.talents ?? []).map(t => (
          t.profileImageUrl || !found.has(t.talentProfileId)
            ? t
            : { ...t, profileImageUrl: found.get(t.talentProfileId) }
        )),
      } : prev));
    })();
    return () => { cancelled = true; };
  }, [partner]);

  const contacts = partner ? [
    partner.email && { type: 'email', label: '이메일', value: partner.email, href: `mailto:${partner.email}` },
    partner.homepage && { type: 'homepage', label: '홈페이지', value: partner.homepage, href: partner.homepage.startsWith('http') ? partner.homepage : `https://${partner.homepage}` },
    partner.instagram && { type: 'instagram', label: '인스타그램', value: partner.instagram, href: socialHref(partner.instagram, 'https://www.instagram.com/') },
    partner.youtube && { type: 'youtube', label: '유튜브', value: partner.youtube, href: socialHref(partner.youtube, 'https://www.youtube.com/@') },
  ].filter(Boolean) : [];

  return (
    <PageLayout>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px 80px' }}>
        <button
          onClick={() => navigate('/partners')}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: '28px' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke={C.sub} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={t('15px', 500, C.sub)}>파트너스</span>
        </button>

        {loading ? (
          <div style={{ padding: '80px 0', display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner size="lg" />
          </div>
        ) : error || !partner ? (
          <div style={{ padding: '60px 24px', textAlign: 'center', border: `1px solid ${C.g200}`, borderRadius: '14px', background: C.bg }}>
            <span style={t('16px', 400, C.sub)}>{error ?? '정보를 찾을 수 없습니다.'}</span>
          </div>
        ) : (
          <>
            {/* 상단 정보 */}
            <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
              <div style={{
                width: '148px', height: '148px', flexShrink: 0, borderRadius: '14px',
                border: `1px solid ${C.g200}`, background: C.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
              }}>
                {/* 이름은 오른쪽에 크게 나오므로, 로고가 없으면 기본 아이콘만 둔다 */}
                <PartnerLogo src={partner.logoUrl} alt={partner.name} background={C.bg} />
              </div>

              <div style={{ flex: 1, minWidth: '260px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <h1 style={{ ...t('32px', 700, C.bk), margin: 0 }}>{partner.name}</h1>
                  <span style={{
                    padding: '5px 10px', borderRadius: '6px', border: `1px solid ${C.g200}`,
                    ...t('11px', 600, C.sub), letterSpacing: '0.04em',
                  }}>
                    CASTAR PARTNER
                  </span>
                  {/* 제작사이면서 소속사인 업체가 있어 종류가 여러 개일 수 있다 */}
                  {partnerTypes(partner).map(type => (
                    <span
                      key={type}
                      style={{
                        padding: '5px 10px', borderRadius: '6px',
                        background: C.bg, border: `1px solid ${C.g200}`,
                        ...t('11px', 600, C.sub), letterSpacing: '0.04em',
                      }}
                    >
                      {PARTNER_TYPE_LABEL[type]}
                    </span>
                  ))}
                </div>

                {partner.tagline && (
                  <span style={{ ...t('16px', 400, C.bk), whiteSpace: 'pre-wrap' }}>{partner.tagline}</span>
                )}

                {partner.region && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" stroke={C.sub} strokeWidth="1.6" />
                      <circle cx="12" cy="10" r="2.5" stroke={C.sub} strokeWidth="1.6" />
                    </svg>
                    <span style={t('15px', 400, C.sub)}>{partner.region}</span>
                  </div>
                )}

                {partner.homepage && (
                  <a
                    href={partner.homepage.startsWith('http') ? partner.homepage : `https://${partner.homepage}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{
                      alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '12px 18px', borderRadius: '10px', border: `1px solid ${C.g200}`,
                      background: C.white, textDecoration: 'none', ...t('15px', 600, C.bk),
                    }}
                  >
                    공식 홈페이지
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d={ICONS.homepage} stroke={C.bk} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </a>
                )}
              </div>
            </div>

            {/* 소개 */}
            {(partner.description || contacts.length > 0) && (
              <section style={{ marginTop: '40px', paddingTop: '32px', borderTop: `1px solid ${C.g200}` }}>
                <h2 style={{ ...t('22px', 700, C.bk), margin: '0 0 16px' }}>소개</h2>
                {partner.description && (
                  <p style={{ ...t('16px', 400, C.bk), lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: '0 0 20px' }}>
                    {partner.description}
                  </p>
                )}
                {contacts.map(c => <ContactRow key={c.type} {...c} />)}
              </section>
            )}

            {/* 종류별 하단 — 여러 종류면 합쳐서 한 번씩만 그린다 */}
            <PartnerSections partner={partner} navigate={navigate} />
          </>
        )}
      </div>
    </PageLayout>
  );
}
